import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ConfirmationModal, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import type { DeleteTaskTemplateModalProps } from './DeleteTaskTemplateModal.types'

export function DeleteTaskTemplateModal({
  isOpen,
  onClose,
  programId,
  taskTemplateId,
  taskTemplateName,
  refresh
}: DeleteTaskTemplateModalProps) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const [{ isPending }, { apiFunc: deleteTaskTemplate }] = useMutation(
    grantProgramService.deleteTaskTemplate,
    () => {
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const handleClose = () => {
    onClose()
    setModalState(null)
  }

  const handleSuccessClose = () => {
    refresh()
    handleClose()
  }

  const renderModalContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.programDetails.taskTemplates.deleteModal.success.title')}
          buttonText={t('button.close')}
          handleClose={handleSuccessClose}
          viewTestId="delete-task-template-success-modal"
        >
          <Text size="small">{t('grants.programDetails.taskTemplates.deleteModal.success.description')}</Text>
        </AcknowledgeModal>
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.programDetails.taskTemplates.deleteModal.error.title')}
          description={t('grants.programDetails.taskTemplates.deleteModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="delete-task-template-error-modal"
        />
      )
    }

    return (
      <ConfirmationModal
        title={t('grants.programDetails.taskTemplates.deleteModal.title')}
        primaryButtonText={t('button.delete')}
        secondaryButtonText={t('button.cancel')}
        loading={isPending}
        handleClose={handleClose}
        handleSubmit={() => deleteTaskTemplate({ grantProgramId: programId, taskTemplateId })}
        primaryBtnTestId="delete-task-template-submit-button"
        secondaryBtnTestId="delete-task-template-cancel-button"
        aria-labelledby="delete-task-template-modal"
      >
        <Text>{t('grants.programDetails.taskTemplates.deleteModal.description', { taskTemplateName })}</Text>
      </ConfirmationModal>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="delete-task-template-modal">
      {renderModalContent()}
    </Modal>
  )
}
